import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Users as UsersIcon } from 'lucide-react';
import type { UserToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useUsersList, useUserRoles, useUserMutations } from '~/hooks/use-users';

/** Users CRUD surface — parity with the legacy admin grid (Role/Email/Name/Surname/Visible/Sort). */
export function UsersGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useUsersList();
	const { data: roles = [] } = useUserRoles();
	const { remove } = useUserMutations();

	const [deleteRow, setDeleteRow] = useState<UserToken | null>(null);

	const roleMap = useMemo(() => new Map(roles.map(r => [r.id, r.nm])), [roles]);
	const roleOf = (u: UserToken) => roleMap.get(u.rid) ?? '';

	const columns = useMemo<SystemGridColumn<UserToken>[]>(
		() => [
			{
				key: 'role',
				header: t('system.users.columns.role'),
				width: '150px',
				sortValue: u => roleOf(u),
				exportValue: u => roleOf(u),
				render: u => <span className="truncate text-sm text-foreground-muted">{roleOf(u) || '—'}</span>
			},
			{
				key: 'email',
				header: t('system.users.columns.email'),
				width: 'minmax(0,1fr)',
				sortValue: u => u.eml,
				exportValue: u => u.eml,
				render: u => <span className="truncate text-sm font-medium text-foreground">{u.eml}</span>
			},
			{
				key: 'firstName',
				header: t('system.users.columns.firstName'),
				width: '150px',
				sortValue: u => u.fn ?? '',
				exportValue: u => u.fn ?? '',
				render: u => <span className="truncate text-sm text-foreground-muted">{u.fn || '—'}</span>
			},
			{
				key: 'lastName',
				header: t('system.users.columns.lastName'),
				width: '150px',
				sortValue: u => u.ln ?? '',
				exportValue: u => u.ln ?? '',
				render: u => <span className="truncate text-sm text-foreground-muted">{u.ln || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.users.columns.visible'),
				width: '70px',
				align: 'center',
				sortValue: u => (u.iv ? 1 : 0),
				exportValue: u => (u.iv ? t('common.yes') : t('common.no')),
				render: u => (u.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.users.columns.sort'),
				width: '60px',
				align: 'right',
				sortValue: u => u.si ?? 0,
				exportValue: u => u.si ?? 0,
				render: u => <span className="text-sm tabular-nums text-foreground-muted">{u.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, roleMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<UserToken>
				gridId="users"
				title={t('system.users.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={u => u.id ?? u.eml}
				columns={columns}
				searchPredicate={(u, q) => `${u.eml ?? ''} ${u.fn ?? ''} ${u.ln ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<UsersIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.users.add')}
				onAdd={() => navigate('/system/users/create')}
				onEdit={u => navigate(`/system/users/${u.id}`)}
				onDelete={setDeleteRow}
				exportFilename="users"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.users.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.users.deleteBody', { name: deleteRow?.eml ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
